import { Container } from '@design-system/container';
import { Typography } from '@design-system/typography';
import classnames from 'classnames';
import React, { forwardRef } from 'react';

import useSiteTree from '~/hooks/useSiteTree';

import TMPLinkWrap from '../TMPLinkWrap';
import { SitemapChildrenProps, SitemapListProps } from './models';

const SitemapChildren = ({
  list,
  level = 1,
  variant = 'body',
  tag,
  size = 'm',
  weight = 'regular',
  sublistSecondLevel,
}: SitemapChildrenProps) =>
  list.length > 0 ? (
    <ul
      className={classnames(
        'sitemap__sublist',
        `sitemap__sublist--level-${level}`
      )}
    >
      {list.map((item) => (
        <li key={item.path}>
          <TMPLinkWrap
            variant="subtle"
            href={item.path}
            className="sitemap__sublist-link"
          >
            <Typography
              className="sitemap__sublist-title"
              content={item.title}
              variant={variant}
              size={size}
              weight={weight}
              tag={tag}
            />
          </TMPLinkWrap>

          <SitemapChildren
            variant={sublistSecondLevel?.variant || variant}
            weight={sublistSecondLevel?.weight || weight}
            size={sublistSecondLevel?.size || size}
            list={item.children}
            level={level + 1}
          />
        </li>
      ))}
    </ul>
  ) : null;

const SitemapList = forwardRef<HTMLDivElement, SitemapListProps>(
  (
    {
      entryTitleField,
      mainLevel,
      sublistFirstLevel,
      sublistSecondLevel,
      locale,
      componentId,
    },
    ref
  ) => {
    const listOfPages = useSiteTree({ lang: locale, entryTitleField });

    return (
      <div id={componentId} className="sitemap" ref={ref}>
        <Container maxWidth="max-grid" className="sitemap__list">
          {listOfPages.map((parent) => (
            <div key={parent.path} className="sitemap__item">
              <TMPLinkWrap
                variant="subtle"
                href={parent.path}
                className="sitemap__link"
              >
                <Typography
                  className="sitemap__title"
                  content={parent.title}
                  variant={mainLevel.variant || 'headline'}
                  size={mainLevel.size}
                  weight={mainLevel.weight}
                  tag="h2"
                />
              </TMPLinkWrap>

              <SitemapChildren
                list={parent.children}
                variant={sublistFirstLevel.variant}
                weight={sublistFirstLevel.weight}
                size={sublistFirstLevel.size}
                tag="h3"
                sublistSecondLevel={sublistSecondLevel}
              />
            </div>
          ))}
        </Container>
      </div>
    );
  }
);

export default SitemapList;
